import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../store";
import { LegsProgress } from "../../PerformanceTile/LegsProgress/LegsProgress";
import { PerformanceTile } from "../../PerformanceTile/PerformanceTile";
import { PerformanceTileHeader } from "../../PerformanceTile/PerformanceTileHeader/PerformanceTileHeader";
import { FetchFaaTrackingData } from "./FaaTrackingDataLayer";
import { maskFaaTrackingData } from "./FaaTrackingTile.mask";
import defaultContent from "../../../../../content/performanceIncomeDashboard";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ROUTE_PATHS } from "../../../../../common/enums/routing-enums";
import { AwardProgressBadge } from "../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { parseStatus, spreadTrend } from "../utils";
import { ActionCardError } from "../../../GroupManagementDashboard/ActionCard/ActionCardError/ActionCardError";
import { useEffect, useState } from "react";
import { getLongPeriodFormat } from "../../../../../common/util/period";
import { storedLocale } from "../../../../Authentication/util";
import "./FaaTrackingTile.scss";

const loader = () => {
  return (
    <PerformanceTile>
      <PerformanceTileHeader label={<LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin="0" />} />
      <LegsProgress
        items={[
          {
            label: <LeafRectangleLoader isLoading width="8rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="6rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="10rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="6rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="8rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
          {
            label: <LeafRectangleLoader isLoading width="6rem" height="1.25rem" margin="0" />,
            value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
          },
        ]}
      />
    </PerformanceTile>
  );
};

export const FaaTrackingTile = () => {
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { faaTrackingData: rawFaaData, isLoading, errorStatus, refetch } = FetchFaaTrackingData(selectedPeriod);
  const showData = useAppSelector((state) => state.showData.showData);
  const faaTrackingData = maskFaaTrackingData(rawFaaData, showData);
  const { t } = useTranslation(["performanceIncomeDashboard"]);

  const desktopFaaTitle = t(
    "performance.hatgar.foundersAchievementAwardTracking",
    defaultContent["performance"]["hatgar"]["foundersAchievementAwardTracking"] as string,
  );
  const mobileFaaTitle = t(
    "performance.hatgar.faaTracking",
    defaultContent["performance"]["hatgar"]["faaTracking"] as string,
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const faaTitle = windowWidth >= 620 ? desktopFaaTitle : mobileFaaTitle;
  const locale = storedLocale();
  const lastMonthProcessed = faaTrackingData.faaTracking.lastMonthProcessed;

  if (isLoading) return loader();

  if (errorStatus) {
    return (
      <PerformanceTile>
        <PerformanceTileHeader link={ROUTE_PATHS.FAA} label={faaTitle} />
        <ActionCardError onRetry={refetch} />
      </PerformanceTile>
    );
  }

  const faaSubtitle = (
    <p className="FaaTrackingTile__last-month-processed">
      {t("performance.hatgar.lastMonthProcessed", defaultContent.performance.hatgar.lastMonthProcessed as string)}:{" "}
      <span>{lastMonthProcessed > 0 ? getLongPeriodFormat(lastMonthProcessed.toString(), locale) : "--"}</span>
    </p>
  );

  return faaTrackingData.faaTracking.showFaa ? (
    <div data-testid="faa-tracking-tile">
      <PerformanceTile>
        <PerformanceTileHeader
          link={ROUTE_PATHS.FAA}
          label={faaTitle}
          tealiumTag="faaAwardTracking"
          subtitle={faaSubtitle}
        />
        <LegsProgress
          items={[
            {
              testid: "faa-row-dbr-status",
              label: t("performance.hatgar.dbrStatus", defaultContent.performance.hatgar.dbrStatus as string),
              value: showData ? (
                <AwardProgressBadge status={parseStatus(faaTrackingData.faaTracking.dbrStatus)} hideIcon />
              ) : (
                "--"
              ),
            },
            {
              testid: "faa-row-fplat-legs",
              label: t("performance.hatgar.fPlatLegs", defaultContent.performance.hatgar.fPlatLegs as string),
              value: faaTrackingData.faaTracking.foundersPlatinumLegs.formatted,
              ...spreadTrend(faaTrackingData.faaTracking.foundersPlatinumLegs.moMTrend),
            },
            {
              testid: "faa-row-faa-points",
              label: t("performance.hatgar.faaPoints", defaultContent.performance.hatgar.faaPoints as string),
              value: faaTrackingData.faaTracking.faaPoints.formatted,
              ...spreadTrend(faaTrackingData.faaTracking.faaPoints.moMTrend),
            },
            {
              testid: "faa-row-faa-multiplier",
              label: t("performance.hatgar.faaMultiplier", defaultContent.performance.hatgar.faaMultiplier as string),
              value: faaTrackingData.faaTracking.faaMultiplier.formatted,
            },
            {
              testid: "faa-row-tracking-bonus",
              label: t("performance.hatgar.superLegs", defaultContent.performance.hatgar.superLegs as string),
              value: faaTrackingData.faaTracking.superLegs.formatted,
            },
          ]}
        />
      </PerformanceTile>
    </div>
  ) : null;
};
